/*
 * QFOOD cart guard (v2) -- assets/old-productincart-check.js
 *
 * Removes cart lines that can no longer be ordered (delivery date in the past,
 * product drafted/archived in Shopify, variant sold out) and, above all, GATES
 * every checkout control in the theme so an expired line can never reach the
 * Shopify checkout domain.
 *
 * Design notes (see also the cart spec):
 *  - The IIFE runs at script-execution time: the checkout interception must be
 *    installed before the customer can click anything. Only the first passive
 *    validation waits for DOMContentLoaded.
 *  - Every failure path fails OPEN (line kept, checkout allowed) EXCEPT a
 *    definite 404/410 from /products/<handle>.js, which is Shopify saying
 *    "this product is not for sale".
 *  - validateCart() never rejects. A guard bug must degrade to "checkout works,
 *    guard did nothing", never to "nobody can check out".
 *  - ES5 syntax only (matches the rest of Dawn's classic scripts).
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) {
    // Keep the theme editor preview fully clickable. There is no real checkout there.
    return;
  }
  if (window.__qfoodCartGuard) {
    // Idempotent: replaces the old __oldProductInCartCheckRan flag.
    return;
  }

  var VERSION = '2';
  var LOG_PREFIX = '[CartGuard]';
  var NOTICE_ID = 'cart-guard-notice';
  var PENDING_CLASS = 'cart-guard-pending';
  var RELOAD_KEY = 'qfood:cartGuard:reload';

  var RESULT_TTL_MS = 3000; // short-circuit window for non-forced runs
  var RELOAD_FLOOR_MS = 60000; // never reload more than once per minute
  var NOTICE_MS = 8000;
  var CHECKOUT_WATCHDOG_MS = 6000; // hard cap: a hung request may not block checkout
  var GATE_RELEASE_MS = 4000; // unconditional release of the accelerated-checkout gate
  var VISIBILITY_STALE_MS = 300000; // 5 minutes hidden -> revalidate on return
  var FETCH_TIMEOUT_MS = 5000; // per-request abort; a hung socket may not wedge the guard
  var STALE_INFLIGHT_MS = 30000; // an in-flight run older than this can only be dead
  var SUBMITTER_TTL_MS = 1500; // how long a recorded click counts as "this submit's submitter"

  // Swedish customer-facing copy.
  var MSG_REMOVED =
    'En eller flera rätter gick inte längre att beställa (datumet har passerat eller de är slutsålda) och har tagits bort ur din varukorg.';
  var MSG_EMPTY = 'Din varukorg är nu tom.';
  var MSG_BLOCKED_PREFIX = 'Vi kunde inte gå vidare till kassan: ';
  var MSG_BUSY = 'varukorgen uppdateras fortfarande. Vänta någon sekund och försök igen.';
  var MSG_CART_EMPTY = 'varukorgen är tom.';
  var MSG_SUBMIT_FAILED = 'sidan hade uppdaterats. Ladda om sidan och försök igen.';

  // Logging: compiled-off by default, switchable on the live shop with
  // localStorage.setItem('cartGuardDebug', '1') so the merchant never has to
  // re-upload the asset to debug.
  var ENABLE_LOGGING =
    false ||
    (function () {
      try {
        return window.localStorage && window.localStorage.getItem('cartGuardDebug') === '1';
      } catch (e) {
        return false;
      }
    })();

  function log() {
    if (!ENABLE_LOGGING) return;
    try {
      var a = Array.prototype.slice.call(arguments);
      a.unshift(LOG_PREFIX);
      console.log.apply(console, a);
    } catch (e) {}
  }

  function warn() {
    if (!ENABLE_LOGGING) return;
    try {
      var a = Array.prototype.slice.call(arguments);
      a.unshift(LOG_PREFIX);
      console.warn.apply(console, a);
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------
  var inFlight = null; // the single in-progress validateCart() promise
  var inFlightToken = null; // identity of the run that owns `inFlight`
  var inFlightStartedAt = 0;
  var removalsInFlight = 0; // >0 => the guard has DECIDED to remove and is writing
  var lastResult = null;
  var lastRunAt = 0;
  var productCache = null; // Map(handle -> promise), rebuilt per validation run
  var noticeEl = null;
  var noticeTextEl = null;
  var noticeTimer = null;
  var gateReleased = false;
  var hiddenSince = 0;

  // ---------------------------------------------------------------------------
  // fetch helpers -- fetchJsonSafe never rejects
  // ---------------------------------------------------------------------------
  function failedFetch() {
    return { status: 0, data: null, networkError: true };
  }

  // Every request gets an abort timeout where AbortController exists. Without it
  // a socket that never answers keeps the single in-flight validation run alive
  // forever, which would silently disable the guard for the rest of the page view.
  function armAbort(opts) {
    var timer = null;
    try {
      if (typeof AbortController === 'function' && !opts.signal) {
        var ctl = new AbortController();
        opts.signal = ctl.signal;
        timer = setTimeout(function () {
          try {
            ctl.abort();
          } catch (e) {}
        }, FETCH_TIMEOUT_MS);
      }
    } catch (e) {
      timer = null;
    }
    return function () {
      if (timer === null) return;
      try {
        clearTimeout(timer);
      } catch (e) {}
      timer = null;
    };
  }

  function fetchJsonSafe(url, options) {
    var opts = options || {};
    if (!opts.credentials) opts.credentials = 'same-origin';
    var clearAbort = armAbort(opts);

    var p;
    try {
      p = fetch(url, opts);
    } catch (e) {
      warn('fetch threw', url, e);
      clearAbort();
      return Promise.resolve(failedFetch());
    }
    if (!p || typeof p.then !== 'function') {
      clearAbort();
      return Promise.resolve(failedFetch());
    }

    return p
      .then(
      function (res) {
        var status = res && typeof res.status === 'number' ? res.status : 0;
        var ct = '';
        try {
          ct = ((res.headers && res.headers.get('content-type')) || '').toLowerCase();
        } catch (e) {
          ct = '';
        }

        // Shopify answers an unknown handle with an HTML 404 page. Treat any
        // HTML body as "no data" but keep the status -- the status is the signal.
        if (ct.indexOf('text/html') !== -1) {
          log('response (html)', url, status);
          return { status: status, data: null, networkError: false };
        }

        var jsonish =
          ct.indexOf('application/json') !== -1 ||
          ct.indexOf('text/javascript') !== -1 ||
          ct.indexOf('application/javascript') !== -1;

        // Body reading must never turn into a rejection: a malformed body is
        // "no data", not "the guard exploded".
        try {
          if (jsonish && typeof res.json === 'function') {
            return res.json().then(
              function (d) {
                return { status: status, data: d, networkError: false };
              },
              function () {
                return { status: status, data: null, networkError: false };
              }
            );
          }

          if (typeof res.text !== 'function') {
            return { status: status, data: null, networkError: false };
          }

          return res.text().then(
            function (t) {
              var parsed = null;
              try {
                parsed = JSON.parse(t);
              } catch (e) {
                parsed = null;
              }
              return { status: status, data: parsed, networkError: false };
            },
            function () {
              return { status: status, data: null, networkError: false };
            }
          );
        } catch (e) {
          warn('body read threw', url, e && e.message ? e.message : String(e));
          return { status: status, data: null, networkError: false };
        }
      },
      function (e) {
        warn('fetch failed', url, e && e.message ? e.message : String(e));
        return failedFetch();
      }
    )
      .then(function (v) {
        clearAbort();
        return v;
      });
  }

  function fetchTextSafe(url) {
    var opts = { credentials: 'same-origin' };
    var clearAbort = armAbort(opts);
    var p;
    try {
      p = fetch(url, opts);
    } catch (e) {
      clearAbort();
      return Promise.resolve(null);
    }
    if (!p || typeof p.then !== 'function') {
      clearAbort();
      return Promise.resolve(null);
    }
    return p
      .then(
        function (res) {
          if (!res || !res.ok || typeof res.text !== 'function') return null;
          return res.text().then(
            function (t) {
              return t;
            },
            function () {
              return null;
            }
          );
        },
        function () {
          return null;
        }
      )
      .then(function (v) {
        clearAbort();
        return v;
      });
  }

  // ---------------------------------------------------------------------------
  // Pure helpers (carried over from v1 -- they were already correct)
  // ---------------------------------------------------------------------------
  function parseStrictYYYYMMDD(value) {
    if (value == null) return null;

    var raw = String(value);
    var m = raw.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
    if (!m) return null;

    var y = Number(m[1]);
    var mo = Number(m[2]);
    var d = Number(m[3]);

    if (!(y >= 1970 && y <= 2100)) return null;
    if (!(mo >= 1 && mo <= 12)) return null;
    if (!(d >= 1 && d <= 31)) return null;

    var dt = new Date(y, mo - 1, d);
    dt.setHours(0, 0, 0, 0);

    // Round-trip check so 2026-02-31 is rejected instead of rolling over.
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;

    return dt;
  }

  function todayLocalMidnight() {
    var t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  function isPastDate(dateObj) {
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return false;
    return dateObj.getTime() < todayLocalMidnight().getTime();
  }

  function getLineDate(item) {
    var props = item && item.properties;
    if (!props) return { date: null, source: 'no_properties', raw: null };

    if (props.Leveransdatum) {
      var dt = parseStrictYYYYMMDD(props.Leveransdatum);
      if (dt) return { date: dt, source: 'properties.Leveransdatum', raw: props.Leveransdatum };
      return { date: null, source: 'properties.Leveransdatum_unparseable', raw: props.Leveransdatum };
    }

    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      var dt2 = parseStrictYYYYMMDD(props[k]);
      if (dt2) return { date: dt2, source: 'properties.' + k, raw: props[k] };
    }

    return { date: null, source: 'properties_no_date_found', raw: null };
  }

  function getHandle(item) {
    if (!item) return null;
    if (item.product_handle) return item.product_handle;
    var url = item.url || '';
    var parts = String(url).split('?')[0].split('/products/');
    var tail = parts.length > 1 ? parts[1] : '';
    if (!tail) return null;
    tail = tail.split('/')[0].split('#')[0];
    return tail || null;
  }

  function htmlToText(html) {
    try {
      var tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return String(tmp.textContent || tmp.innerText || '').trim();
    } catch (e) {
      return String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .trim();
    }
  }

  // ---------------------------------------------------------------------------
  // Classification
  // ---------------------------------------------------------------------------
  function cachedProductFetch(handle, cache) {
    var key = String(handle);
    if (cache && typeof cache.get === 'function' && cache.has(key)) return cache.get(key);
    var p = fetchJsonSafe('/products/' + encodeURIComponent(key) + '.js', { cache: 'no-store' });
    if (cache && typeof cache.set === 'function') cache.set(key, p);
    return p;
  }

  // -> Promise<'remove'|'keep'>
  function classifyLine(item, cache) {
    var info = getLineDate(item);

    // 1. Fast path: the Leveransdatum line-item property. Costs no request.
    if (info.date && isPastDate(info.date)) {
      log('remove (past line property date)', item && item.key, info.raw);
      return Promise.resolve('remove');
    }

    // 2. No handle -> nothing to check. Fail open.
    var handle = getHandle(item);
    if (!handle) {
      warn('no product handle on line; keeping', item && item.key);
      return Promise.resolve('keep');
    }

    // 3. Product JSON is the source of truth.
    return cachedProductFetch(handle, cache).then(function (res) {
      // FAIL OPEN: a CDN hiccup must never empty a customer's cart.
      if (res.networkError || res.status >= 500) {
        warn('product fetch failed, keeping', handle, res.status);
        return 'keep';
      }

      // FAIL CLOSED: Shopify serves 404 for a DRAFT/archived product. That IS
      // the POS saying "not for sale".
      if (res.status === 404 || res.status === 410) {
        log('remove (product 404/410)', handle, res.status);
        return 'remove';
      }

      if (res.status === 200 && res.data) {
        var dd = parseStrictYYYYMMDD(htmlToText(res.data.description));
        if (dd && isPastDate(dd)) {
          log('remove (past description date)', handle);
          return 'remove';
        }
        if (res.data.available === false) {
          log('remove (product unavailable)', handle);
          return 'remove';
        }
        // This shop models pickup points as variants: the product can be
        // available while THE chosen pickup point is gone.
        var variants = res.data.variants || [];
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (v && v.id === item.variant_id) {
            if (v.available === false) {
              log('remove (variant unavailable)', handle, item.variant_id);
              return 'remove';
            }
            break;
          }
        }
        return 'keep';
      }

      return 'keep';
    });
  }

  // ---------------------------------------------------------------------------
  // Removal -- SEQUENTIAL. /cart/change.js serialises writes to one cart and
  // concurrent calls answer 422.
  // ---------------------------------------------------------------------------
  function removeLines(keys) {
    var state = { removed: 0, cart: null, failures: 0 };

    return keys
      .reduce(function (p, key) {
        return p.then(function () {
          return fetchJsonSafe('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          }).then(function (r) {
            if (r.status === 200) {
              state.removed += 1;
              if (r.data) state.cart = r.data;
            } else {
              state.failures += 1;
              warn('remove failed', key, r.status);
            }
            return r;
          });
        });
      }, Promise.resolve())
      .then(function () {
        return state;
      });
  }

  // ---------------------------------------------------------------------------
  // UI refresh -- mirrors assets/cart.js instead of reloading blindly
  // ---------------------------------------------------------------------------
  function cartUrl() {
    try {
      if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    } catch (e) {}
    return '/cart';
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function fetchSection(sectionId) {
    return fetchTextSafe(cartUrl() + '?section_id=' + encodeURIComponent(sectionId));
  }

  function replaceContents(targetEl, selector) {
    var sectionId = (targetEl.dataset && targetEl.dataset.id) || targetEl.id;
    return fetchSection(sectionId).then(function (html) {
      if (html == null) return;
      var source = parseHTML(html).querySelector(selector);
      if (!source) return;
      var target = targetEl.querySelector(selector) || targetEl;
      target.innerHTML = source.innerHTML;
    });
  }

  function refreshCartUI(cart) {
    var jobs = [];
    var itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;

    try {
      // 1. Cart icon bubble -- always.
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        jobs.push(
          fetchSection('cart-icon-bubble').then(function (html) {
            if (html == null) return;
            var src = parseHTML(html).querySelector('.shopify-section');
            if (src) bubble.innerHTML = src.innerHTML;
          })
        );
      }

      // 2. Cart page sections (see cart.js updateQuantity / getSectionsToRender).
      var mainItems = document.getElementById('main-cart-items');
      var mainFooter = document.getElementById('main-cart-footer');
      if (mainItems) {
        jobs.push(replaceContents(mainItems, '.js-contents'));
        var cartItemsEl = document.querySelector('cart-items');
        if (cartItemsEl && cartItemsEl.classList && itemCount !== null) {
          cartItemsEl.classList.toggle('is-empty', itemCount === 0);
        }
      }
      if (mainFooter) {
        jobs.push(replaceContents(mainFooter, '.js-contents'));
        if (mainFooter.classList && itemCount !== null) {
          mainFooter.classList.toggle('is-empty', itemCount === 0);
        }
      }

      // 3. Cart drawer (cart_type is 'notification' today, but the setting is
      //    one click away). Identical to cart.js onCartUpdate.
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        if (drawer.classList && itemCount !== null) drawer.classList.toggle('is-empty', itemCount === 0);
        jobs.push(
          fetchSection('cart-drawer').then(function (html) {
            if (html == null) return;
            var doc = parseHTML(html);
            var selectors = ['cart-drawer-items', '.cart-drawer__footer'];
            for (var i = 0; i < selectors.length; i++) {
              var target = document.querySelector(selectors[i]);
              var source = doc.querySelector(selectors[i]);
              if (target && source && target.replaceWith) target.replaceWith(source);
            }
          })
        );
      }

      // 4. Close the add-to-cart popup: its checkout button must not be clicked
      //    against a cart that just changed underneath it.
      var notif = document.getElementById('cart-notification');
      if (notif && notif.classList && notif.classList.contains('active')) {
        var host = document.querySelector('cart-notification');
        if (host && typeof host.close === 'function') host.close();
      }
    } catch (e) {
      warn('refreshCartUI failed', e);
      safeReload();
      return Promise.resolve();
    }

    return Promise.all(jobs).then(
      function () {
        // For any future consumer. Deliberately NOT PUB_SUB_EVENTS.cartUpdate:
        // the guard subscribes to that event and would re-enter validateCart.
        try {
          document.dispatchEvent(new CustomEvent('cart-guard:updated', { detail: { cart: cart || null } }));
        } catch (e) {}
      },
      function (e) {
        warn('refreshCartUI section fetch failed', e);
        safeReload();
      }
    );
  }

  // The ONLY place a reload can happen, and it is rate-limited. A storage
  // failure degrades to "no reload", never to "reload every time".
  function safeReload() {
    var now = Date.now();
    try {
      var raw = window.sessionStorage.getItem(RELOAD_KEY);
      var last = raw ? Number(raw) : 0;
      if (now - last < RELOAD_FLOOR_MS) {
        warn('reload suppressed (rate limit)');
        return false;
      }
      window.sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch (e) {
      warn('reload suppressed (storage unavailable)');
      return false;
    }
    try {
      window.location.reload();
    } catch (e) {}
    return true;
  }

  // ---------------------------------------------------------------------------
  // Notice -- ONE reusable node
  // ---------------------------------------------------------------------------
  function buildNotice() {
    var node = document.createElement('div');
    node.id = NOTICE_ID;
    if (typeof node.setAttribute === 'function') {
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
    }
    var s = node.style;
    s.position = 'fixed';
    s.bottom = '20px';
    s.right = '20px';
    s.maxWidth = 'min(92vw, 34rem)';
    s.display = 'flex';
    s.alignItems = 'flex-start';
    s.gap = '1rem';
    s.padding = '1rem 1.2rem';
    s.background = 'rgba(0,0,0,.88)';
    s.color = '#fff';
    s.fontSize = '1.4rem';
    s.lineHeight = '1.4';
    s.borderRadius = '.4rem';
    s.boxShadow = '0 .4rem 1.2rem rgba(0,0,0,.3)';
    s.zIndex = '10000';

    var text = document.createElement('span');
    node.appendChild(text);

    var close = document.createElement('button');
    close.type = 'button';
    if (typeof close.setAttribute === 'function') close.setAttribute('aria-label', 'Stäng');
    close.textContent = '×';
    var cs = close.style;
    cs.background = 'none';
    cs.border = '0';
    cs.color = 'inherit';
    cs.cursor = 'pointer';
    cs.fontSize = '2rem';
    cs.lineHeight = '1';
    cs.padding = '0';
    if (typeof close.addEventListener === 'function') close.addEventListener('click', hideNotice);
    node.appendChild(close);

    var host = document.body || document.documentElement;
    if (host && typeof host.appendChild === 'function') host.appendChild(node);

    noticeEl = node;
    noticeTextEl = text;
  }

  function hideNotice() {
    if (!noticeEl) return;
    try {
      noticeEl.style.display = 'none';
    } catch (e) {}
  }

  function showNotice(message) {
    try {
      if (!noticeEl) buildNotice();
      if (!noticeEl || !noticeTextEl) return;
      noticeTextEl.textContent = message;
      noticeEl.style.display = 'flex';
      if (noticeTimer) clearTimeout(noticeTimer);
      noticeTimer = setTimeout(hideNotice, NOTICE_MS);
    } catch (e) {
      warn('showNotice failed', e);
    }
  }

  function removalMessage(cart, prefix) {
    var msg = (prefix || '') + MSG_REMOVED;
    if (cart && cart.item_count === 0) msg += ' ' + MSG_EMPTY;
    return msg;
  }

  // ---------------------------------------------------------------------------
  // Accelerated / dynamic checkout buttons cannot be intercepted (cross-origin
  // iframes, Shopify-owned custom elements). Gate them until the first
  // validation settles, and release the gate unconditionally after
  // GATE_RELEASE_MS so a stuck fetch can never permanently disable a payment
  // button. Nothing renders them in this shop today (show_dynamic_checkout is
  // false and cart.json has no additional_checkout_buttons block); this is
  // future-proofing that costs nothing.
  // ---------------------------------------------------------------------------
  function installAcceleratedGate() {
    try {
      document.documentElement.classList.add(PENDING_CLASS);
      var style = document.createElement('style');
      style.textContent =
        'html.' + PENDING_CLASS + ' .additional-checkout-buttons,' +
        'html.' + PENDING_CLASS + ' shopify-accelerated-checkout,' +
        'html.' + PENDING_CLASS + ' shopify-accelerated-checkout-cart,' +
        'html.' + PENDING_CLASS + ' .shopify-payment-button{pointer-events:none;opacity:.45;}' +
        // Busy state for the intercepted checkout button. Deliberately keeps the
        // label readable (see setBusy).
        '[data-cart-guard-busy]{cursor:progress;opacity:.75;}';
      var head = document.head || document.documentElement;
      if (head && head.appendChild) head.appendChild(style);
    } catch (e) {}
    try {
      setTimeout(releaseAcceleratedGate, GATE_RELEASE_MS);
    } catch (e) {}
  }

  function releaseAcceleratedGate() {
    if (gateReleased) return;
    gateReleased = true;
    try {
      document.documentElement.classList.remove(PENDING_CLASS);
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // validateCart -- the public API. Never rejects.
  // ---------------------------------------------------------------------------
  function runValidation() {
    productCache = new Map();
    var cache = productCache;

    return fetchJsonSafe('/cart.js', { cache: 'no-store' }).then(function (r) {
      // FAIL OPEN.
      if (r.networkError || r.status !== 200 || !r.data) {
        warn('could not read /cart.js', r.status);
        return { removed: 0, cart: null, failed: true };
      }

      var cart = r.data;
      var items = cart.items;
      if (!items || !items.length) {
        log('cart empty; nothing to do');
        return { removed: 0, cart: cart, failed: false };
      }

      var checks = [];
      for (var i = 0; i < items.length; i++) checks.push(classifyLine(items[i], cache));

      return Promise.all(checks).then(function (verdicts) {
        var keys = [];
        var seen = {};
        for (var j = 0; j < verdicts.length; j++) {
          if (verdicts[j] !== 'remove') continue;
          var key = items[j] && items[j].key;
          if (!key || seen[key]) continue;
          seen[key] = true;
          keys.push(key);
        }

        if (!keys.length) return { removed: 0, cart: cart, failed: false };

        log('removing lines', keys);
        // From here on the guard has DECIDED. The checkout watchdog must block
        // instead of failing open while these writes are in flight, or the
        // customer reaches checkout with the stale line still in the cart.
        removalsInFlight += 1;
        function removalsDone() {
          if (removalsInFlight > 0) removalsInFlight -= 1;
        }
        return removeLines(keys).then(
          function (state) {
            removalsDone();
            if (state.removed > 0) showNotice(removalMessage(state.cart, ''));
            return refreshCartUI(state.cart).then(function () {
              return {
                removed: state.removed,
                cart: state.cart || cart,
                failed: state.failures > 0
              };
            });
          },
          function (e) {
            removalsDone();
            throw e;
          }
        );
      });
    });
  }

  function validateCart(options) {
    var opts = options || {};

    // Never two concurrent runs, even when forced -- UNLESS the in-flight run is
    // so old it can only be dead (a request that never settles on a browser
    // without AbortController). Without this escape hatch one hung fetch would
    // disable the guard for the rest of the page view.
    if (inFlight) {
      if (Date.now() - inFlightStartedAt < STALE_INFLIGHT_MS) return inFlight;
      warn('discarding a stale in-flight run started', Date.now() - inFlightStartedAt, 'ms ago');
      inFlight = null;
      inFlightToken = null;
    }

    if (!opts.force && lastResult && Date.now() - lastRunAt < RESULT_TTL_MS) {
      log('short-circuit: reusing result from', Date.now() - lastRunAt, 'ms ago');
      return Promise.resolve(lastResult);
    }

    var token = {};
    var run;
    try {
      run = runValidation();
    } catch (e) {
      run = Promise.reject(e);
    }

    run = run
      .then(null, function (e) {
        warn('validateCart failed', e && e.message ? e.message : String(e));
        return { removed: 0, cart: null, failed: true };
      })
      .then(function (result) {
        var safe = result || { removed: 0, cart: null, failed: true };
        // A discarded (stale) run must never clobber the run that replaced it.
        if (inFlightToken === token) {
          inFlight = null;
          inFlightToken = null;
          lastRunAt = Date.now();
          lastResult = safe;
        }
        releaseAcceleratedGate();
        return safe;
      });

    inFlight = run;
    inFlightToken = token;
    inFlightStartedAt = Date.now();
    return run;
  }

  // A hung request must never block checkout forever. After the watchdog we
  // fail OPEN, consistent with every other failure path in the guard.
  function validateForCheckout() {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = null;

      function finish(res) {
        if (settled) return;
        settled = true;
        if (timer) {
          try {
            clearTimeout(timer);
          } catch (e) {}
        }
        resolve(res);
      }

      try {
        timer = setTimeout(function () {
          if (removalsInFlight > 0) {
            // The guard has already classified a line as "remove" and is writing
            // /cart/change.js. Failing open here would (a) send the customer to
            // checkout with the expired line still in the cart and (b) race those
            // writes against the checkout navigation. Block and let them retry.
            warn('checkout watchdog fired while removals are in flight; blocking');
            finish({ removed: 0, cart: null, failed: true, timedOut: true, blocked: true });
            return;
          }
          warn('checkout watchdog fired after', CHECKOUT_WATCHDOG_MS, 'ms; allowing checkout');
          finish({ removed: 0, cart: null, failed: true, timedOut: true });
        }, CHECKOUT_WATCHDOG_MS);
      } catch (e) {}

      validateCart({ force: true }).then(
        function (res) {
          finish(res || { removed: 0, cart: null, failed: true });
        },
        function () {
          finish({ removed: 0, cart: null, failed: true });
        }
      );
    });
  }

  // ---------------------------------------------------------------------------
  // Checkout interception -- installed at script-execution time
  // ---------------------------------------------------------------------------
  // event.submitter polyfill. Safari < 15.4 does not implement it, and BOTH
  // checkout buttons in this theme are rendered OUTSIDE the form they submit
  // (<button name="checkout" form="cart"> in main-cart-footer.liquid,
  // <button name="checkout" form="CartDrawer-Form"> in cart-drawer.liquid), so a
  // form-scoped querySelector can never find them. Recording the last clicked
  // submit control is also the only way to tell "clicked Kassa" apart from
  // "pressed Enter in a quantity field" -- guessing would turn a quantity update
  // into a checkout.
  var lastSubmitter = null;
  var lastSubmitterAt = 0;

  function isSubmitControl(el) {
    var tag = el && el.tagName ? String(el.tagName).toUpperCase() : '';
    if (tag !== 'BUTTON' && tag !== 'INPUT') return false;
    var type = '';
    try {
      type = (el.getAttribute && el.getAttribute('type')) || el.type || '';
    } catch (e) {
      type = '';
    }
    type = String(type).toLowerCase();
    if (tag === 'BUTTON') return type !== 'button' && type !== 'reset';
    return type === 'submit' || type === 'image';
  }

  function recordSubmitter(e) {
    try {
      var el = e && e.target;
      var depth = 0;
      while (el && depth < 25) {
        if (el.tagName && (String(el.tagName).toUpperCase() === 'BUTTON' || String(el.tagName).toUpperCase() === 'INPUT')) {
          if (isSubmitControl(el)) {
            lastSubmitter = el;
            lastSubmitterAt = Date.now();
          }
          return;
        }
        el = el.parentNode;
        depth += 1;
      }
    } catch (err) {}
  }

  function ownsSubmitter(form, el) {
    if (!form || !el) return false;
    try {
      if (el.form) return el.form === form;
    } catch (e) {}
    try {
      var attr = el.getAttribute && el.getAttribute('form');
      if (attr) return attr === form.id;
    } catch (e) {}
    try {
      if (typeof form.contains === 'function') return form.contains(el);
    } catch (e) {}
    return false;
  }

  // Honours the `form` attribute, so it finds a checkout button that lives
  // outside the form element it submits.
  function findCheckoutButton(form) {
    if (!form) return null;
    try {
      if (typeof form.querySelector === 'function') {
        var inside = form.querySelector('button[name="checkout"], input[name="checkout"]');
        if (inside) return inside;
      }
    } catch (e) {}
    try {
      if (form.id && /^[A-Za-z][\w-]*$/.test(form.id) && document.querySelector) {
        var outside = document.querySelector(
          'button[name="checkout"][form="' + form.id + '"], input[name="checkout"][form="' + form.id + '"]'
        );
        if (outside) return outside;
      }
    } catch (e) {}
    return null;
  }

  function findCheckoutSubmit(e) {
    var form = e && e.target;
    if (!form) return null;

    var submitter = e.submitter || null;
    if (
      !submitter &&
      lastSubmitter &&
      Date.now() - lastSubmitterAt < SUBMITTER_TTL_MS &&
      ownsSubmitter(form, lastSubmitter)
    ) {
      submitter = lastSubmitter;
    }

    var byName = !!(submitter && submitter.name === 'checkout');
    var byForm = form.id === 'cart-notification-form';
    var action = typeof form.getAttribute === 'function' ? form.getAttribute('action') || '' : '';
    var postsToCart = /\/cart\/?$/.test(String(action).split('?')[0].split('#')[0]);

    // postsToCart is the real safety net: ANY POST to the cart URL is gated,
    // even when the submitter cannot be identified at all. (The old
    // `postsToCart && byName` clause could never change the outcome.)
    if (!byName && !byForm && !postsToCart) return null;
    return { form: form, submitter: submitter };
  }

  function setBusy(el, busy) {
    if (!el) return;
    try {
      if (busy) {
        el.setAttribute('aria-disabled', 'true');
        // NOT Dawn's `.loading`: base.css sets `.button.loading{color:transparent}`
        // and the spinner only renders through a `.loading__spinner` child, which
        // no checkout button in this theme has. It would blank the label out for
        // up to CHECKOUT_WATCHDOG_MS on every single checkout click.
        el.setAttribute('data-cart-guard-busy', '1');
      } else {
        el.removeAttribute('aria-disabled');
        el.removeAttribute('data-cart-guard-busy');
      }
    } catch (e) {}
  }

  function isDetached(el) {
    try {
      return !!el && el.isConnected === false;
    } catch (e) {
      return false;
    }
  }

  // cart.js onCartUpdate replaces <cart-items>' innerHTML and the guard's own
  // refreshCartUI replaces the footer contents, so the form and the button
  // captured at click time can both be dead by the time we get here.
  function resolveLiveForm(form) {
    if (!form) return null;
    if (!isDetached(form)) return form;
    try {
      if (form.id && document.getElementById) {
        var live = document.getElementById(form.id);
        if (live) return live;
      }
    } catch (e) {}
    return null;
  }

  function resolveLiveSubmitter(form, submitter) {
    if (submitter && !isDetached(submitter)) return submitter;
    return findCheckoutButton(form) || null;
  }

  // -> true when a submission was actually started.
  function proceedWithSubmit(form, submitter) {
    var target = resolveLiveForm(form);
    if (!target) {
      warn('checkout form is gone; cannot resubmit');
      return false;
    }
    var btn = resolveLiveSubmitter(target, submitter);
    var hidden = null;

    // The pass-through flag MUST expire with the current task. requestSubmit()
    // runs interactive constraint validation and fires NO submit event when a
    // field is invalid (cart.js setValidity() leaves a customValidity on the
    // quantity inputs), and form.submit() never fires one at all -- either way a
    // sticky flag would pre-authorise the NEXT, completely un-validated click.
    function expirePass() {
      try {
        if (target.dataset && target.dataset.cartGuardOk === '1') delete target.dataset.cartGuardOk;
      } catch (e) {
        try {
          target.dataset.cartGuardOk = '';
        } catch (e2) {}
      }
      if (hidden) {
        try {
          if (hidden.parentNode && hidden.parentNode.removeChild) hidden.parentNode.removeChild(hidden);
        } catch (e) {}
        hidden = null;
      }
    }

    try {
      if (target.dataset) target.dataset.cartGuardOk = '1';
    } catch (e) {}
    try {
      setTimeout(expirePass, 0);
    } catch (e) {}

    // form.submit() DROPS the submitter's name/value, which would POST /cart
    // without checkout=... -- Shopify answers that with a plain cart re-render,
    // i.e. the checkout button would appear to do nothing.
    if (typeof target.requestSubmit === 'function') {
      try {
        target.requestSubmit(btn || undefined);
        return true;
      } catch (e) {
        // NotFoundError: the submitter's form owner changed under us. Fall through
        // rather than dying inside a .then() with the checkout button left dead.
        warn('requestSubmit threw; falling back to form.submit()', e && e.message ? e.message : String(e));
      }
    }

    // Only re-add a name when we actually know the submitter: fabricating
    // name="checkout" would turn an anonymous POST /cart (a quantity update)
    // into a checkout.
    if (btn && btn.name) {
      try {
        var inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = btn.name;
        inp.value = btn.value || '';
        target.appendChild(inp);
        hidden = inp;
      } catch (e) {}
    }
    if (typeof target.submit === 'function') {
      try {
        target.submit();
        return true;
      } catch (e) {
        warn('form.submit() threw', e && e.message ? e.message : String(e));
      }
    }
    expirePass();
    return false;
  }

  function onCheckoutSubmit(e) {
    var hit = findCheckoutSubmit(e);
    if (!hit) return;

    var form = hit.form;
    if (form.dataset && form.dataset.cartGuardOk === '1') {
      // Our own resubmit: consume the flag and let it through.
      try {
        delete form.dataset.cartGuardOk;
      } catch (err) {
        form.dataset.cartGuardOk = '';
      }
      return;
    }

    if (typeof e.preventDefault === 'function') e.preventDefault();
    if (typeof e.stopPropagation === 'function') e.stopPropagation();

    setBusy(hit.submitter, true);

    validateForCheckout().then(function (res) {
      setBusy(hit.submitter, false);

      if (res && res.blocked) {
        showNotice(MSG_BLOCKED_PREFIX + MSG_BUSY);
        return;
      }
      if (res && res.removed > 0) {
        // Do NOT auto-continue: the customer must see what changed and click again.
        showNotice(removalMessage(res.cart, MSG_BLOCKED_PREFIX));
        return;
      }
      if (res && res.cart && res.cart.item_count === 0) {
        showNotice(MSG_BLOCKED_PREFIX + MSG_CART_EMPTY);
        return;
      }

      if (!proceedWithSubmit(form, hit.submitter)) showNotice(MSG_BLOCKED_PREFIX + MSG_SUBMIT_FAILED);
    });
  }

  // Anchor links straight to /checkout (e.g. a hand-written "Till kassan" link).
  function isCheckoutHref(href) {
    if (!href) return false;
    var s = String(href);
    if (s.charAt(0) === '#' || s.toLowerCase().indexOf('javascript:') === 0) return false;
    var path = s.split('#')[0].split('?')[0];
    var m = path.match(/^https?:\/\/[^/]+(\/.*)?$/i);
    if (m) path = m[1] || '/';
    return /(^|\/)checkout\/?$/.test(path);
  }

  function closestAnchor(node) {
    var el = node;
    var depth = 0;
    while (el && depth < 25) {
      if (el.tagName && String(el.tagName).toUpperCase() === 'A') return el;
      el = el.parentNode;
      depth += 1;
    }
    return null;
  }

  function onDocumentClick(e) {
    if (!e) return;
    recordSubmitter(e);
    if (e.defaultPrevented) return;
    if (typeof e.button === 'number' && e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = closestAnchor(e.target);
    if (!a || typeof a.getAttribute !== 'function') return;

    var href = a.getAttribute('href');
    if (!isCheckoutHref(href)) return;

    if (a.getAttribute('data-cart-guard-ok') === '1') {
      a.removeAttribute('data-cart-guard-ok');
      return;
    }

    if (typeof e.preventDefault === 'function') e.preventDefault();
    if (typeof e.stopPropagation === 'function') e.stopPropagation();

    validateForCheckout().then(function (res) {
      if (res && res.blocked) {
        showNotice(MSG_BLOCKED_PREFIX + MSG_BUSY);
        return;
      }
      if (res && res.removed > 0) {
        showNotice(removalMessage(res.cart, MSG_BLOCKED_PREFIX));
        return;
      }
      if (res && res.cart && res.cart.item_count === 0) {
        showNotice(MSG_BLOCKED_PREFIX + MSG_CART_EMPTY);
        return;
      }
      try {
        a.setAttribute('data-cart-guard-ok', '1');
        window.location.href = a.href || href;
      } catch (err) {}
    });
  }

  // ---------------------------------------------------------------------------
  // Triggers
  // ---------------------------------------------------------------------------
  function watchCartOpen() {
    if (typeof MutationObserver !== 'function') return;

    var targets = [];
    try {
      var notif = document.getElementById('cart-notification');
      if (notif) targets.push(notif);
      var drawer = document.querySelector('cart-drawer');
      if (drawer) targets.push(drawer);
    } catch (e) {}
    if (!targets.length) return;

    var obs = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (!t || !t.classList) continue;
        var open = t.classList.contains('active');
        if (open && !t.__cartGuardOpen) {
          t.__cartGuardOpen = true;
          log('cart popup/drawer opened; revalidating');
          validateCart({ force: true });
        } else if (!open) {
          t.__cartGuardOpen = false;
        }
      }
    });

    for (var j = 0; j < targets.length; j++) {
      try {
        obs.observe(targets[j], { attributes: true, attributeFilter: ['class'] });
      } catch (e) {}
    }
  }

  function onReady() {
    // 1. Passive first pass.
    validateCart({ force: false });

    // 2. Re-run after every add-to-cart / quantity change.
    //    NOTE: constants.js declares PUB_SUB_EVENTS with `const`, which creates a
    //    LEXICAL global, not a property of window -- window.PUB_SUB_EVENTS is
    //    undefined forever, in every load order. Only the bare identifier
    //    resolves, hence `typeof PUB_SUB_EVENTS` and never window.PUB_SUB_EVENTS.
    var evName =
      typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate
        ? PUB_SUB_EVENTS.cartUpdate
        : 'cart-update';

    if (typeof subscribe === 'function') {
      subscribe(evName, function (ev) {
        if (ev && ev.source === 'cart-guard') return;
        validateCart({ force: true });
      });
    } else {
      warn('pubsub not available; cart-update validation disabled');
    }

    // 3. Cart drawer / notification popup opening.
    watchCartOpen();

    // 4. Tab left open across the vendor's closing time.
    try {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
          hiddenSince = Date.now();
          return;
        }
        if (hiddenSince && Date.now() - hiddenSince > VISIBILITY_STALE_MS) {
          hiddenSince = 0;
          validateCart({ force: true });
        }
      });
    } catch (e) {}

    // 5. bfcache back-navigation, e.g. the browser back button from checkout.
    try {
      window.addEventListener('pageshow', function (ev) {
        if (ev && ev.persisted) validateCart({ force: true });
      });
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Install
  // ---------------------------------------------------------------------------
  window.__qfoodCartGuard = {
    version: VERSION,
    validateCart: validateCart,
    __test: {
      fetchJsonSafe: fetchJsonSafe,
      parseStrictYYYYMMDD: parseStrictYYYYMMDD,
      todayLocalMidnight: todayLocalMidnight,
      isPastDate: isPastDate,
      getLineDate: getLineDate,
      getHandle: getHandle,
      htmlToText: htmlToText,
      classifyLine: classifyLine,
      cachedProductFetch: cachedProductFetch,
      removeLines: removeLines,
      refreshCartUI: refreshCartUI,
      safeReload: safeReload,
      showNotice: showNotice,
      removalMessage: removalMessage,
      isCheckoutHref: isCheckoutHref,
      findCheckoutSubmit: findCheckoutSubmit,
      findCheckoutButton: findCheckoutButton,
      ownsSubmitter: ownsSubmitter,
      proceedWithSubmit: proceedWithSubmit,
      validateForCheckout: validateForCheckout,
      onReady: onReady,
      constants: {
        RESULT_TTL_MS: RESULT_TTL_MS,
        RELOAD_FLOOR_MS: RELOAD_FLOOR_MS,
        NOTICE_MS: NOTICE_MS,
        CHECKOUT_WATCHDOG_MS: CHECKOUT_WATCHDOG_MS,
        GATE_RELEASE_MS: GATE_RELEASE_MS,
        FETCH_TIMEOUT_MS: FETCH_TIMEOUT_MS,
        STALE_INFLIGHT_MS: STALE_INFLIGHT_MS,
        SUBMITTER_TTL_MS: SUBMITTER_TTL_MS,
        MSG_REMOVED: MSG_REMOVED,
        MSG_EMPTY: MSG_EMPTY,
        MSG_BLOCKED_PREFIX: MSG_BLOCKED_PREFIX,
        MSG_BUSY: MSG_BUSY,
        MSG_CART_EMPTY: MSG_CART_EMPTY,
        MSG_SUBMIT_FAILED: MSG_SUBMIT_FAILED
      }
    }
  };
  // Friendlier alias for console debugging; same instance.
  window.QfoodCartGuard = window.__qfoodCartGuard;

  installAcceleratedGate();

  // Capture phase, on document: cart-notification.js renderContents() and
  // cart.js updateQuantity() replace section innerHTML on every cart mutation,
  // so any listener bound to a checkout button dies on the first add-to-cart.
  // Delegation is mandatory, not stylistic.
  document.addEventListener('submit', onCheckoutSubmit, true);
  document.addEventListener('click', onDocumentClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
